import {
  Annotation,
  BarPieChartData,
  DataPoint,
  Document,
  FMILanguage,
  FMIProduct,
  Language,
  User,
} from "../index";

import { parseUtcAsLocal } from "app/shared/utils/date-utils";

export class ManualsDownloadedData extends BarPieChartData {
  public DataPoints: ManualDownloadedDataPoint[];

  constructor(data?: Partial<ManualsDownloadedData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new ManualDownloadedDataPoint(dp)) || [];
  }
}

export class ManualDownloadedDataPoint extends DataPoint {
  public FMIProduct: FMIProduct;
  public Document: Document;

  constructor(data?: Partial<ManualDownloadedDataPoint>) {
    super();
    Object.assign(this, data);

    if (data?.Document) {
      this.Document = new Document(data.Document);
    }
  }
}
export class ManualsDownloadedDataIncluded {
  static readonly All = "All";
  static readonly Most = "Most";
  static readonly Least = "Least";
  static readonly NoDownloads = "NoDownloads";
}

export interface PagesViewed {
  [key: number]: number;
}

export interface SearchTermsUsed {
  [key: string]: number;
}

export class ManualsOpenedData extends BarPieChartData {
  public DataPoints: ManualOpenDataPoint[];

  constructor(data?: Partial<ManualsOpenedData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new ManualOpenDataPoint(dp)) || [];
  }
}

export class UserWithDate {
  public User: User;
  public DateOpened: Date;
  public Userid: Number;

  constructor(data?: Partial<UserWithDate>) {
    Object.assign(this, data);
    if (data?.DateOpened) this.DateOpened = parseUtcAsLocal(data.DateOpened);
  }
}

export class ManualOpenDataPoint extends ManualDownloadedDataPoint {
  public PagesViewed: PagesViewed;
  public SearchTermsUsed: SearchTermsUsed;
  public DatesOpened: UserWithDate[];

  constructor(data?: Partial<ManualOpenDataPoint>) {
    super();
    Object.assign(this, data);
    this.DatesOpened = data?.DatesOpened?.map((d) => new UserWithDate(d)) || [];

    if (data?.Document) {
      this.Document = new Document(data.Document);
    }
  }
}

export class ManualsCurrentlyOpenData extends BarPieChartData {
  public DataPoints: ManualCurrentlyOpenDataPoint[];

  constructor(data?: Partial<ManualsCurrentlyOpenData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new ManualCurrentlyOpenDataPoint(dp)) || [];
  }
}

export class ManualCurrentlyOpenDataPoint extends ManualOpenDataPoint {
  public Users: User[];

  constructor(data?: Partial<ManualCurrentlyOpenDataPoint>) {
    super(data);
    Object.assign(this, data);
  }
}

export class ManualsCreatedOrUpdatedData extends BarPieChartData {
  public DataPoints: ManualCreatedOrUpdatedData[];

  constructor(data?: Partial<ManualsCreatedOrUpdatedData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new ManualCreatedOrUpdatedData(dp)) || [];
  }
}

export class ManualCreatedOrUpdatedData extends ManualDownloadedDataPoint {
  public CreatedByUser: string;
  public UpdatedByUser: string;

  constructor(data?: Partial<ManualCreatedOrUpdatedData>) {
    super();
    Object.assign(this, data);
    if (data?.Document) {
      this.Document = new Document(data.Document);
    }
  }
}

export class ManualsPrintedData extends BarPieChartData {
  public DataPoints: ManualPrintedData[];

  constructor(data?: Partial<ManualsPrintedData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new ManualPrintedData(dp)) || [];
  }
}

interface IntToDatesDict {
  [key: number]: Date[];
}

interface IntToStringDict {
  [key: number]: string;
}

export class ManualPrintedData extends DataPoint {
  public Document: Document;
  public PrintedByUsers: User[];
  public UserIdToDatesDict: IntToDatesDict;

  constructor(data?: Partial<ManualPrintedData>) {
    super();
    Object.assign(this, data);

    if (data?.UserIdToDatesDict) {
      this.UserIdToDatesDict = {};
      for (const key in data.UserIdToDatesDict) {
        this.UserIdToDatesDict[key] = data.UserIdToDatesDict[key].map((d) =>
          parseUtcAsLocal(d)
        );
      }
    }
    if (data?.Document) {
      this.Document = new Document(data.Document);
    }
  }
}

export class AnnotationsSentData extends BarPieChartData {
  public DataPoints: AnnotationSentData[];

  constructor(data?: Partial<AnnotationsSentData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new AnnotationSentData(dp)) || [];
  }
}

export class AnnotationSentData extends ManualDownloadedDataPoint {
  public UserIdToDatesDict: IntToDatesDict;
  public SentByUsers: User[];
  public Annotations: Annotation[];

  constructor(data?: Partial<AnnotationSentData>) {
    super();
    Object.assign(this, data);

    if (data?.UserIdToDatesDict) {
      this.UserIdToDatesDict = {};
      for (const key in data.UserIdToDatesDict) {
        this.UserIdToDatesDict[key] = data.UserIdToDatesDict[key].map((d) =>
          parseUtcAsLocal(d)
        );
      }
    }

    if (data?.Document) {
      this.Document = new Document(data.Document);
    }
  }
}

export class SearchTermsUsedData extends BarPieChartData {
  public DataPoints: SearchTermUsedData[];

  constructor(data?: Partial<SearchTermsUsedData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new SearchTermUsedData(dp)) || [];
  }
}

export class SearchTermUsedData extends DataPoint {
  public DocumentsSearched: Document[];
  public SearchTerm: string;

  constructor(data?: Partial<SearchTermUsedData>) {
    super();
    Object.assign(this, data);

    this.DocumentsSearched =
      data?.DocumentsSearched?.map((d) => new Document(d)) || [];
  }
}

export class PagesOfManualsViewedData extends BarPieChartData {
  public DataPoints: PageOfManualsViewedData[];

  constructor(data?: Partial<PagesOfManualsViewedData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new PageOfManualsViewedData(dp)) || [];
  }
}

export class PageOfManualsViewedData extends DataPoint {
  public Document: Document;
  public PageNumberViewed: number;

  constructor(data?: Partial<PageOfManualsViewedData>) {
    super();
    Object.assign(this, data);

    if (data?.Document) {
      this.Document = new Document(data.Document);
    }
  }
}

export class ManualLanguagesData extends BarPieChartData {
  public DataPoints: ManualLanguageData[];

  constructor(data?: Partial<ManualLanguagesData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new ManualLanguageData(dp)) || [];
  }
}

export class ManualLanguageData extends DataPoint {
  public Language: FMILanguage;

  constructor(data?: Partial<ManualLanguageData>) {
    super();
    Object.assign(this, data);
  }
}

export class GlobalSearchTermsData extends BarPieChartData {
  public DataPoints: GlobalSearchTermDataPoint[];
}

export class GlobalSearchTermDataPoint extends DataPoint {
  public SearchTerm: string;
  public Total: number;
}

export class GlobalSearchTermDocumentsData extends BarPieChartData {
  public DataPoints: GlobalSearchTermDocumentDataPoint[];

  constructor(data?: Partial<GlobalSearchTermDocumentsData>) {
    super();
    Object.assign(this, data);
    this.DataPoints =
      data?.DataPoints?.map((dp) => new GlobalSearchTermDocumentDataPoint(dp)) || [];
  }
}

export class GlobalSearchTermDocumentDataPoint extends DataPoint {
  public SearchTerm: string;
  public Document: Document;
  public Total: number;

  constructor(data?: Partial<GlobalSearchTermDocumentDataPoint>) {
    super();
    Object.assign(this, data);

    if (data?.Document) {
      this.Document = new Document(data.Document);
    }
  }
}
